<script setup lang="ts">
import type { CollectionListItemFragmentWithChildren } from '@layers/common/app/stores/collections'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const localePath = useLocalePath()
const { t } = useI18n()
const { ecommerce } = useAnalytics()
const collectionsStore = useCollectionsStore()

// Check if collections are loading
const isLoadingCollections = computed(() => collectionsStore.rootCollections.status === 'pending')

// Pick the first 3 collections to show on tabs
const mainCollections = computed(() => collectionsStore.rootCollections.data?.slice(0, 3))

const currentCollection = ref<CollectionListItemFragmentWithChildren | undefined>(undefined)
const currentRootCollectionId = ref<string>('all')
const searchQueryInput = ref('')

const currentRootCollection = computed(() => collectionsStore.collectionTree.get(currentRootCollectionId.value))
const showFirstLevelCollectionNavigation = computed(() => !currentCollection.value)

// Watch for tab changes to load child collections
watch(currentRootCollectionId, async (newId) => {
  if (newId !== 'all') {
    // Load children for the selected collection
    await collectionsStore.loadChildCollections(newId)
  }
})

// Delay child collection loading to prevent blocking the drawer
onMounted(() => {
  // Load child collections for visible root collections
  if (collectionsStore.rootCollections.data) {
    // Queue the loading of child collections after drawer opens
    setTimeout(() => {
      // Fetch all child collections in parallel since the drawer is already open
      const promises = collectionsStore.rootCollections.data!.map(collection =>
        collectionsStore.loadChildCollections(collection.id.toString()),
      )
      Promise.all(promises).catch((error) => {
        console.error('Error loading child collections:', error)
      })
    }, 400)
  }
})

// Helper to check if a collection's children are being loaded
function isLoadingChildren(collectionId: string) {
  return collectionsStore.loadingCollections.has(collectionId)
}

// Helper to check if we should show skeleton for a collection's children
function shouldShowChildrenSkeleton(collection: CollectionListItemFragmentWithChildren) {
  // Don't show skeleton if children have already been loaded (even if empty)
  return !collectionsStore.loadedChildren.has(collection.id.toString()) && !collection.children.size
}

function handleFirstLevelCollectionNavigationClick(collection: CollectionListItemFragmentWithChildren) {
  currentCollection.value = collection
}

function handleCollectionClickBack() {
  currentCollection.value = undefined
}

async function handleSecondLevelCollectionClick(collection: CollectionListItemFragmentWithChildren) {
  emit('close')
  navigateTo(localePath(`/collections/${collection.id}/${collection.slug}`))
}

async function handleSearchSubmit() {
  let path = localePath('/search')

  if (currentRootCollection.value && currentRootCollectionId.value !== 'all')
    path = localePath(`/collections/${currentRootCollection.value.id}/${currentRootCollection.value.slug}`)

  emit('close')
  ecommerce.search(searchQueryInput.value)
  navigateTo({ path, query: { q: searchQueryInput.value } })
}
</script>

<template>
  <section class="flex-1 px5 pb2.5">
    <RdxTabsRoot v-model="currentRootCollectionId" class="flex flex-col" default-value="all">
      <RdxTabsList
        v-if="showFirstLevelCollectionNavigation"
        class="relative flex shrink-0 overflow-y-auto border-b n-border-base px5 -mx5"
        aria-label="Collections"
      >
        <RdxTabsIndicator
          class="absolute bottom-0 left-0 h0.75 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] rounded-full transition-all duration-150"
        >
          <div class="h-full w-full bg-slate-900" />
        </RdxTabsIndicator>

        <RdxTabsTrigger
          class="mr5 h13 flex cursor-default select-none items-center justify-center rounded-tl-md text-base n-text-body leading-none outline-none hover:text-slate-500 !n-active:text-slate-900 !n-active:fw700"
          value="all"
        >
          {{ t('collections.all') }}
        </RdxTabsTrigger>
        <RdxTabsTrigger
          v-for="collection in mainCollections"
          :key="collection.id"
          class="mr5 h13 flex cursor-default select-none items-center justify-center rounded-tl-md text-base n-text-body leading-none outline-none hover:text-slate-500 !n-active:text-slate-900 !n-active:fw700"
          :value="collection.id"
        >
          {{ collection.name }}
        </RdxTabsTrigger>
      </RdxTabsList>

      <form
        v-if="showFirstLevelCollectionNavigation"
        @submit.prevent="handleSearchSubmit"
      >
        <div class="mt4 flex items-center border n-border-base rd bg-slate100 px2 focus-within:border-context focus-within:n-focus-base">
          <Icon name="i-heroicons:magnifying-glass" class="mr-0.4em text-1.1em op50" />
          <input
            v-model="searchQueryInput"
            type="search"
            class="wfull flex-auto bg-transparent py2.5 fw600 !outline-none"
            :placeholder="t('search.collection_placeholder')"
          >
        </div>
      </form>

      <div class="mt3 -my5" />

      <RdxTabsContent class="relative flex of-hidden" value="all">
        <Transition
          enter-active-class="duration-150 ease-[cubic-bezier(0.81,0.08,0.68,0.53)] top-0"
          enter-from-class="transform op0 translate-x-[-20%] absolute top-0"
          enter-to-class="op100 translate-x-0 top-0"
          leave-active-class="duration-150 ease-[cubic-bezier(0.81,0.08,0.68,0.53)] top-0"
          leave-from-class="op100 translate-x-0 top-0"
          leave-to-class="transform op0 translate-x-[-20%] absolute top-0"
        >
          <div
            v-if="showFirstLevelCollectionNavigation"
            :data-active="showFirstLevelCollectionNavigation"
            class="w-full"
          >
            <nav class="mb2.5 mt6">
              <menu
                class="pointer-events-auto grid grid-cols-[repeat(1,1fr)] m0 list-none gap1 p0 op100 xs:grid-cols-[repeat(2,1fr)]"
                data-testid="firstLevelCollectionNavigation"
              >
                <!-- Show loading skeleton while collections are loading -->
                <template v-if="isLoadingCollections">
                  <div v-for="n in 6" :key="`skeleton-${n}`" class="relative flex items-center justify-between gap-2 of-hidden rd px4 py3">
                    <NSkeleton class="h4 grow-1" />
                    <div class="h11 min-w-[2rem] flex shrink-0 items-center justify-center">
                      <NSkeleton class="h-full w-full rd-0.5" />
                    </div>
                  </div>
                </template>
                <!-- Show collections when loaded -->
                <template v-else>
                  <CollectionTreeNavigationItem
                    v-for="[key, value] in collectionsStore.collectionTree" :key="`all-${key}`"
                    :collection="value"
                    prevent
                    @click="handleFirstLevelCollectionNavigationClick"
                  />
                </template>
              </menu>
            </nav>
          </div>

          <div
            v-else
            :data-active="!showFirstLevelCollectionNavigation"
            class="top-0"
          >
            <nav v-if="!showFirstLevelCollectionNavigation">
              <div class="mr6 flex justify-between py4.5">
                <button
                  data-testid="collectionNavigationBack"
                  class="cursor-pointer appearance-none border-unset bg-transparent p0"
                  @click="handleCollectionClickBack"
                >
                  <Icon name="i-ri:arrow-left-s-line" class="block shrink-0 grow-0 text-2xl" />
                </button>
                <span class="line-clamp-2 grow-1 text-center text-xl fw-bold">
                  {{ currentCollection?.name }}
                </span>
              </div>
              <menu data-testid="secondLevelCollectionNavigation" class="pointer-events-auto grid grid-cols-[repeat(1,1fr)] m0 list-none gap1 p0 op100 xs:grid-cols-[repeat(2,1fr)]">
                <!-- Add a show all collection which shows all, within the current collection -->
                <template v-if="currentCollection">
                  <CollectionTreeNavigationItem :collection="currentCollection">
                    <span class="line-clamp-2 grow-1 items-center text-ellipsis break-anywhere text-xs text-slate-900 fw600 leading-snug hyphens-auto">
                      {{ t('general.show_all') }}
                    </span>
                  </CollectionTreeNavigationItem>

                  <!-- Show skeleton if children are loading -->
                  <template v-if="shouldShowChildrenSkeleton(currentCollection) || isLoadingChildren(currentCollection.id)">
                    <div v-for="n in 4" :key="`skeleton-child-${n}`" class="relative flex items-center justify-between gap-2 of-hidden rd px4 py3">
                      <NSkeleton class="h4 grow-1" />
                      <div class="h11 min-w-[2rem] flex shrink-0 items-center justify-center">
                        <NSkeleton class="h-full w-full rd-0.5" />
                      </div>
                    </div>
                  </template>
                  <!-- Show actual children if loaded -->
                  <template v-else>
                    <CollectionTreeNavigationItem
                      v-for="[key, value] in currentCollection.children" :key="`all-${key}`"
                      :collection="value"
                      @click="handleSecondLevelCollectionClick"
                    />
                  </template>
                </template>
              </menu>
            </nav>
          </div>
        </Transition>
      </RdxTabsContent>

      <RdxTabsContent
        v-for="collection in mainCollections"
        :key="collection.id"
        class="relative flex of-hidden"
        :value="collection.id"
      >
        <Transition
          enter-active-class="duration-150 ease-[cubic-bezier(0.81,0.08,0.68,0.53)] top-0"
          enter-from-class="transform op0 translate-x-[-20%] absolute top-0"
          enter-to-class="op100 translate-x-0 top-0"
          leave-active-class="duration-150 ease-[cubic-bezier(0.81,0.08,0.68,0.53)] top-0"
          leave-from-class="op100 translate-x-0 top-0"
          leave-to-class="transform op0 translate-x-[-20%] absolute top-0"
        >
          <div
            v-if="showFirstLevelCollectionNavigation"
            :data-active="showFirstLevelCollectionNavigation"
          >
            <nav class="mb2.5 mt6">
              <menu
                class="pointer-events-auto grid grid-cols-[repeat(1,1fr)] m0 list-none gap1 p0 op100 xs:grid-cols-[repeat(2,1fr)]"
                data-testid="firstLevelCollectionNavigation"
              >
                <!-- Show loading skeleton while collections are loading -->
                <template v-if="isLoadingCollections">
                  <div v-for="n in 6" :key="`skeleton-${n}`" class="relative flex items-center justify-between gap-2 of-hidden rd px4 py3">
                    <NSkeleton class="h4 grow-1" />
                    <div class="h11 min-w-[2rem] flex shrink-0 items-center justify-center">
                      <NSkeleton class="h-full w-full rd-0.5" />
                    </div>
                  </div>
                </template>
                <!-- Show collections when loaded -->
                <template v-else-if="collectionsStore.collectionTree.get(collection.id.toString())">
                  <!-- Show skeleton if children haven't been loaded yet -->
                  <template v-if="shouldShowChildrenSkeleton(collectionsStore.collectionTree.get(collection.id.toString())!)">
                    <div v-for="n in 4" :key="`skeleton-${collection.id}-${n}`" class="relative flex items-center justify-between gap-2 of-hidden rd px4 py3">
                      <NSkeleton class="h4 grow-1" />
                      <div class="h11 min-w-[2rem] flex shrink-0 items-center justify-center">
                        <NSkeleton class="h-full w-full rd-0.5" />
                      </div>
                    </div>
                  </template>
                  <!-- Show actual children if loaded -->
                  <template v-else-if="collectionsStore.collectionTree.get(collection.id.toString())!.children.size">
                    <CollectionTreeNavigationItem
                      v-for="[key, value] in collectionsStore.collectionTree.get(collection.id.toString())!.children" :key="`${collection.id}-${key}`"
                      :collection="value"
                      prevent
                      @click="handleFirstLevelCollectionNavigationClick"
                    />
                  </template>
                </template>
              </menu>
            </nav>
          </div>

          <div
            v-else
            :data-active="!showFirstLevelCollectionNavigation"
          >
            <nav v-if="!showFirstLevelCollectionNavigation">
              <div class="mr6 flex justify-between py4.5">
                <button
                  data-testid="collectionNavigationBack"
                  class="cursor-pointer appearance-none border-unset bg-transparent p0"
                  @click="handleCollectionClickBack"
                >
                  <Icon name="i-ri:arrow-left-s-line" class="block shrink-0 grow-0 text-2xl" />
                </button>
                <span class="line-clamp-2 grow-1 text-center text-xl fw-bold">
                  {{ currentCollection?.name }}
                </span>
              </div>
              <menu data-testid="secondLevelCollectionNavigation" class="pointer-events-auto grid grid-cols-[repeat(1,1fr)] m0 list-none gap1 p0 op100 xs:grid-cols-[repeat(2,1fr)]">
                <template v-if="currentCollection">
                  <CollectionTreeNavigationItem :collection="currentCollection">
                    <span class="line-clamp-2 grow-1 items-center text-ellipsis break-anywhere text-xs text-slate-900 fw600 leading-snug hyphens-auto">
                      {{ t('general.show_all') }}
                    </span>
                  </CollectionTreeNavigationItem>

                  <!-- Show skeleton if children are loading -->
                  <template v-if="shouldShowChildrenSkeleton(currentCollection) || isLoadingChildren(currentCollection.id)">
                    <div v-for="n in 4" :key="`skeleton-child-${collection.id}-${n}`" class="relative flex items-center justify-between gap-2 of-hidden rd px4 py3">
                      <NSkeleton class="h4 grow-1" />
                      <div class="h11 min-w-[2rem] flex shrink-0 items-center justify-center">
                        <NSkeleton class="h-full w-full rd-0.5" />
                      </div>
                    </div>
                  </template>
                  <!-- Show actual children if loaded -->
                  <template v-else>
                    <CollectionTreeNavigationItem
                      v-for="[key, value] in currentCollection.children" :key="`${collection.id}-${key}`"
                      :collection="value"
                      @click="handleSecondLevelCollectionClick"
                    />
                  </template>
                </template>
              </menu>
            </nav>
          </div>
        </Transition>
      </RdxTabsContent>
    </RdxTabsRoot>

    <div
      v-if="showFirstLevelCollectionNavigation"
      data-testid="actionContainer"
      class="flex justify-between gap1 border-t n-border-base px5 pb3 pt8 -mx5"
    >
      <!--      <LanguageCountrySwitch /> -->
    </div>
    <div class="test mt-auto">
      <NavDrawerBanner />
    </div>
  </section>
</template>
